import { CheckCircle2 } from 'lucide-react';
import { z } from 'zod';
import { Button } from './Button';
import { Surface } from './Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import { Center, Stack } from './Layout';
import { Radio, RadioGroup } from './RadioGroup';
import { Select } from './Select';
import { Text } from './Text';

const FORM_ID = 'email-report-form';

export type EmailReportFormat = 'CSV' | 'PDF';
export type EmailReportSendMode = 'now' | 'later';
export type EmailReportRecurrence =
  | 'ONE_TIME'
  | 'DAILY'
  | 'BI_WEEKLY'
  | 'WEEKLY'
  | 'MONTHLY';

const RECURRENCES: { value: EmailReportRecurrence; label: string }[] = [
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'DAILY', label: 'Every Day' },
  { value: 'BI_WEEKLY', label: 'Every Two Weeks' },
  { value: 'WEEKLY', label: 'Every Week' },
  { value: 'MONTHLY', label: 'Every Month' },
];

export interface EmailReportScheduleFieldsetProps {
  /** The dialog's form; its schema is `emailReportSchema`. */
  form: ZodForm<typeof emailReportSchema>;
  /** An IANA zone name, shown so the user knows whose clock the time is on. */
  timezone: string;
}

/**
 * The when-to-send block of the email-report dialog: recurrence, date and
 * time on a tinted surface, with the timezone the time will be read in.
 *
 *   <EmailReportScheduleFieldset form={form} timezone="America/Phoenix" />
 */
export function EmailReportScheduleFieldset({
  form,
  timezone,
}: EmailReportScheduleFieldsetProps) {
  return (
    <Surface tone="info" radius="lg" padding="md">
      <Stack gap={4}>
        <FormField form={form} name="scheduleType" label="Recurrence">
          {(field) => (
            <Select
              options={RECURRENCES.map((r) => ({
                value: r.value,
                label: r.label,
              }))}
              value={field.value}
              onValueChange={field.onChange}
            />
          )}
        </FormField>

        <FormField
          form={form}
          name="scheduledDate"
          label="Date"
          description="Defaults to today"
        >
          {(field) => <Input type="date" {...field} />}
        </FormField>

        <FormField
          form={form}
          name="scheduledTime"
          label="Time"
          description={
            <>
              Timezone:{' '}
              <Text as="span" variant="caption-strong" tone="inherit">
                {timezone}
              </Text>
            </>
          }
        >
          {(field) => <Input type="time" {...field} />}
        </FormField>
      </Stack>
    </Surface>
  );
}

/**
 * What the dialog will accept on its own. Whether the typed addresses parse,
 * and whether a scheduled time is still in the future, are the container's
 * to say — it owns those validators — so it reports them with `setError`.
 */
export const emailReportSchema = z
  .object({
    format: z.enum(['CSV', 'PDF']).default('CSV'),
    /** The recipients box as typed: addresses separated by commas or semicolons. */
    recipients: z.string().min(1, 'Enter at least one email address.'),
    sendMode: z.enum(['now', 'later']).default('now'),
    scheduleType: z
      .enum(['ONE_TIME', 'DAILY', 'BI_WEEKLY', 'WEEKLY', 'MONTHLY'])
      .default('ONE_TIME'),
    scheduledDate: z.string(),
    scheduledTime: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.sendMode !== 'later') return;
    if (!values.scheduledDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledDate'],
        message: 'Pick a date to send it on.',
      });
    }
    if (!values.scheduledTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledTime'],
        message: 'Pick a time to send it at.',
      });
    }
  });

export type EmailReportFormValues = z.infer<typeof emailReportSchema>;

export interface EmailReportDialogViewProps {
  open: boolean;
  onClose: () => void;
  /** From `useZodForm(emailReportSchema, …)`. */
  form: ZodForm<typeof emailReportSchema>;
  /** How many of the typed addresses parse; shown as a tick when above zero. */
  validEmailCount: number;
  /** The typed addresses that do not parse; named under the box. */
  invalidEmails: string[];
  timezone: string;
  /** Replaces the form with a confirmation once the report has gone. */
  success?: boolean;
  /** Locks Cancel and spins the submit while the send or schedule runs. */
  busy?: boolean;
  onSubmit: (values: EmailReportFormValues) => void;
}

/**
 * The email-report dialog: pick a format, name the recipients, send now or
 * schedule, and the success state that replaces the form once it has gone.
 *
 *   const form = useZodForm(emailReportSchema, { defaultValues: EMPTY_FORM });
 *   <EmailReportDialogView open={open} onClose={close} form={form}
 *     validEmailCount={valid.length} invalidEmails={invalid}
 *     timezone={tz} busy={sending} onSubmit={submit} />
 *
 * The container parses the recipients and validates the schedule; this
 * shows what it found.
 */
export function EmailReportDialogView({
  open,
  onClose,
  form,
  validEmailCount,
  invalidEmails,
  timezone,
  success = false,
  busy = false,
  onSubmit,
}: EmailReportDialogViewProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Email Report</DialogTitle>
          <DialogDescription>
            Send your towing report to one or more recipients
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <Center direction="column" gap={4} pad="sm">
            <CheckCircle2 className="size-icon-xl text-success" />
            <Text variant="body-sm-strong" align="center">
              Report{' '}
              {form.getValues('sendMode') === 'now' ? 'sent' : 'scheduled'}{' '}
              successfully!
            </Text>
          </Center>
        ) : (
          <>
            <Form form={form} gap={6} id={FORM_ID} onSubmit={onSubmit}>
              <Stack gap={3}>
                <Text variant="h4">Report Format</Text>
                <FormField form={form} name="format">
                  {(field) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <Radio value="CSV" label="CSV (Spreadsheet)" />
                      <Radio value="PDF" label="PDF (Document)" />
                    </RadioGroup>
                  )}
                </FormField>
              </Stack>

              <FormField form={form} name="recipients" label="Email Recipients">
                {(field) => (
                  <Stack gap={1}>
                    <Input
                      {...field}
                      placeholder="Enter email addresses (comma or semicolon separated)"
                      aria-invalid={invalidEmails.length > 0 || undefined}
                    />
                    {validEmailCount > 0 && (
                      <Text variant="caption" tone="success">
                        ✓ {validEmailCount} valid email
                        {validEmailCount !== 1 ? 's' : ''}
                      </Text>
                    )}
                    {invalidEmails.length > 0 && (
                      <Text variant="caption" tone="warning">
                        ⚠ {invalidEmails.length} invalid:{' '}
                        {invalidEmails.join(', ')}
                      </Text>
                    )}
                  </Stack>
                )}
              </FormField>

              <Stack gap={3}>
                <Text variant="h4">Send Options</Text>
                <FormField form={form} name="sendMode">
                  {(field) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <Radio value="now" label="Send Now" />
                      <Radio value="later" label="Schedule for Later" />
                    </RadioGroup>
                  )}
                </FormField>
              </Stack>

              {form.watch('sendMode') === 'later' && (
                <EmailReportScheduleFieldset form={form} timezone={timezone} />
              )}
            </Form>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button form={FORM_ID} type="submit" loading={busy}>
                {form.watch('sendMode') === 'now'
                  ? 'Send Now'
                  : 'Schedule Report'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
